<template>
  <div class="flex flex-col min-h-full sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-md pt-28 mt-2">
      <div class="px-4 bg-white shadow sm:rounded-lg sm:px-10">
        <div class="py-2">
          <div class="py-2">
            <EZRackLogo/>
          </div>
          <Divider/>
          <div class="py-2 text-center text-lg font-semibold text-gray-500">New Password</div>
          <div class="pt-3 pb-2">
            <RMSPassword
              ref="passwordRef"
              :fieldId="'password'"
              :config="{ label: 'Password', feedback: true }"
              :validInputRegEx="validationUtilities.passwordReset().regEx"
              :validInputDescription="validationUtilities.passwordReset().description"
              @onValidInput="onValidInputPassword"
              @onClear="onClearInputPassword"
            />
          </div>
          <div class="pt-2 pb-3">
            <RMSPassword
              ref="passwordConfirmationRef"
              :fieldId="'password_confirmation'"
              :config="{ label: 'Password Confirmation', feedback: false, placeholder: 'Type password again' }"
              :validInputRegEx="passwordConfirmationValidInput ? passwordConfirmationValidInput.regEx : undefined"
              :validInputDescription="passwordConfirmationValidInput ? passwordConfirmationValidInput.description : undefined"
              @onValidInput="onValidInputPasswordConfirmation"
              @onClear="onClearInputPasswordConfirmation"
            />
          </div>
          <div class="pt-10 pb-4">
            <button
              :disabled="!isFormValid"
              class="w-full submit-new-password-button font-medium"
              @click="onSubmit"
            >Submit New Password
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import AuthenticationService from '@/services/auth/AuthenticationService'
import authenticationErrors from '@/contracts/AuthenticationErrors'
import EZRackLogo from '@/components/design/EZRackLogo.vue'
import Divider from 'primevue/divider'
import RMSPassword from '@/components/fields/RMSPassword.vue'
import validationUtilities from '@/utilities/validationUtilities'

export default {
  components: {
    RMSPassword,
    Divider,
    EZRackLogo,
  },
  props: {
    passwordResetObject: {
      type: Object,
      required: true,
    }
  },
  setup(props) {
    const colorMode = ref('dark_sans_company')
    const toast = useToast()
    const authenticationService = new AuthenticationService()
    const passwordRef = ref(null)
    const passwordConfirmationRef = ref(null)
    const formData = ref({
      password: undefined,
      password_confirmation: undefined,
      ...props.passwordResetObject,
    })

    const isFormValid = computed(() => {
      return !!formData.value.password
        && !!formData.value.password_confirmation
        && formData.value.password === formData.value.password_confirmation
        && (!!passwordRef.value && passwordRef.value.inputIsValid)
        && (!!passwordConfirmationRef.value && passwordConfirmationRef.value.inputIsValid)
    })

    const passwordConfirmationValidInput = computed(() => {
      return !!formData.value.password ? validationUtilities.passwordResetConfirmation(formData.value.password) : undefined
    })

    const onValidInputPassword = (value) => formData.value.password = value
    const onClearInputPassword = () => formData.value.password = undefined

    const onValidInputPasswordConfirmation = (value) => formData.value.password_confirmation = value
    const onClearInputPasswordConfirmation = () => formData.value.password_confirmation = undefined

    const onSubmit = () => {
      authenticationService.resetPassword(formData.value)
        .then(response => !!response.success ? onPasswordResetSuccess() : showError(response))
        .catch(error => showError(error.response))
    }

    const onPasswordResetSuccess = () => {
      const life = 5000
      toast.add({
        severity: 'success',
        detail: 'Your password has successfully been reset. Please wait...',
        life: life,
      })
      setTimeout(() => window.location.href = '/', life)
    }

    /**
     * @param {object} response
     */
    const showError = (response) => {
      let detail, severity, summary, life
      if (!!response.code) {
        switch (response.code) {
          case authenticationErrors.FailedPasswordReset:
            severity = 'error'
            summary = 'Error'
            detail = response.message
            life = 3000
            break
          default:
            severity = 'error'
            summary = 'Error'
            detail = response.message ?? 'An error has occurred. Please contact your system administrator.'
            life = 3000
            break
        }
      } else if (!!response?.data?.message) {
        severity = 'error'
        summary = 'Error'
        detail = response?.data?.message
        life = 3000
      }
      toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: life,
      })
    }

    return {
      colorMode,
      formData,
      passwordRef,
      onValidInputPassword,
      onClearInputPassword,
      passwordConfirmationRef,
      onValidInputPasswordConfirmation,
      onClearInputPasswordConfirmation,
      passwordConfirmationValidInput,
      isFormValid,
      onSubmit,
      validationUtilities,
    }
  },
}
</script>
<style lang="scss">

.submit-new-password-button,
.submit-new-password-button:disabled,
.submit-new-password-button:hover {
  border-radius: 3px;
  background-color: #0a6f3f;
  color: white;
  font-size: 1rem;
  height: 3.6rem;
}

.submit-new-password-button:disabled {
  opacity: 1;
}

.submit-new-password-button:hover {
  opacity: 0.95;
}

</style>
